/**
 * =============================================================================
 * ROASTBREW - EMAIL VERIFICATION PAGE
 * =============================================================================
 *
 * Public page rendered at /verify-email?token=xxx
 * Reads the token from the URL, POSTs it to the backend, and shows the result.
 *
 * THEME: pre-auth, so it takes its colours from publicTheme.js — not
 * ThemeContext, which has no tier to read before sign-in. See that file.
 *
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { T, PublicTheme } from './publicTheme';
import PublicMark from './PublicMark';


export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { API_URL } = useAuth();

  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No verification token found. Please check your email link.');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Verification failed');
        }

        setStatus('success');
        setMessage(data.message || 'Your email has been verified.');

      } catch (err) {
        setStatus('error');
        setMessage(err.message);
      }
    };

    verify();
  }, [token, API_URL]);

  return (
    <div
      className="rb-public"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <PublicTheme />
      <div className="rb-ambient" />

      <PublicMark style={{ marginBottom: 'var(--s-5)' }} />

      <div
        className="rb-enter"
        style={{
          textAlign: 'center', maxWidth: '480px', width: '100%',
          position: 'relative', zIndex: 1,
        }}
      >

        {status === 'loading' && (
          <>
            <div
              className="rb-spinner"
              style={{ width: '48px', height: '48px', margin: '0 auto var(--s-4)' }}
            />
            <h1 className="rb-h">Verifying your email</h1>
            <p className="rb-note">This will only take a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <StatusGlyph tone="success" />
            <h1 className="rb-h">Email verified</h1>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              {message}
            </p>
            <button className="rb-btn rb-btn--gold" onClick={() => navigate('/')}>
              Sign in
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <StatusGlyph tone="error" />
            <h1 className="rb-h">Verification failed</h1>
            <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
              {message}
            </p>
            <button className="rb-btn rb-btn--ghost" onClick={() => navigate('/')}>
              Go home
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// STATUS GLYPH
// =============================================================================
// A tinted disc carrying a tick or a cross. Local to this file because it is
// the only place with a terminal success/failure state to report; the shared
// stylesheet stays free of one-off ornament.

function StatusGlyph({ tone }) {
  const color = tone === 'success' ? T.success : T.danger;

  return (
    <div style={{
      width: '72px', height: '72px', borderRadius: '50%',
      background: `${color}12`, border: `.5px solid ${color}38`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto var(--s-3)',
    }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        {tone === 'success'
          ? <path d="M20 6L9 17l-5-5" />
          : <><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></>}
      </svg>
    </div>
  );
}
